
// Simple, centralized business logic used across features. Minimal surface, no over-abstraction.

export type WarrantyValidation = {
  isValid: boolean;
  status: 'valid' | 'expired' | 'not_found';
  daysRemaining: number;
};

// Warranty rules kept deliberately simple. You can enrich later if needed.
export const WarrantyManager = {
  // Accepts a composite key like `${invoiceNo}-${productId}`
  validateWarranty: (compositeKey: string): WarrantyValidation => {
    // Fallback deterministic behavior: valid for 365 days from a hash-derived pseudo date
    // This avoids external calls and keeps UI unblocked. Replace when backend endpoints are ready.
    const hash = Array.from(compositeKey).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    const purchaseEpoch = 1_700_000_000_000; // fixed base epoch for determinism
    const pseudoPurchase = new Date(purchaseEpoch + (hash % 30) * 24 * 60 * 60 * 1000);
    const expiry = new Date(pseudoPurchase.getTime() + 365 * 24 * 60 * 60 * 1000);
    const now = new Date();

    if (now > expiry) {
      return { isValid: false, status: 'expired', daysRemaining: 0 };
    }
    const days = Math.max(0, Math.ceil((expiry.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));
    return { isValid: true, status: 'valid', daysRemaining: days };
  },
};

// Very lightweight workflow utility to unblock approval flows across modules
export type WorkflowRequest = {
  id: string;
  type: string;
  createdAt: string;
  createdBy: string;
  payload?: unknown;
  status: 'pending' | 'approved' | 'rejected';
};

export const WorkflowManager = {
  createApprovalRequest: (type: string, payload: unknown, createdBy: string): WorkflowRequest => {
    return {
      id: `wf_${Date.now()}`,
      type,
      createdAt: new Date().toISOString(),
      createdBy,
      payload,
      status: 'pending',
    };
  },
};

// Comprehensive Approval Rules Engine
export type ApprovalRule = {
  type: string;
  conditions: {
    amountRange?: { min: number; max: number };
    department?: string[];
    priority?: 'low' | 'medium' | 'high' | 'critical';
  };
  approvers: {
    level: number;
    role: string;
    required: boolean;
    canSkip?: boolean;
  }[];
  escalationRules: {
    timeoutHours: number;
    escalateTo: string;
  };
};

export const ApprovalRulesEngine = {
  // Define comprehensive approval rules for different transaction types
  rules: {
    // Invoice Approvals
    invoice: [
      {
        type: 'invoice_small',
        conditions: { amountRange: { min: 0, max: 50000 } },
        approvers: [
          { level: 1, role: 'store_manager', required: true },
          { level: 2, role: 'branch_manager', required: true }
        ],
        escalationRules: { timeoutHours: 24, escalateTo: 'regional_manager' }
      },
      {
        type: 'invoice_medium',
        conditions: { amountRange: { min: 50001, max: 200000 } },
        approvers: [
          { level: 1, role: 'store_manager', required: true },
          { level: 2, role: 'branch_manager', required: true },
          { level: 3, role: 'regional_manager', required: true }
        ],
        escalationRules: { timeoutHours: 48, escalateTo: 'sales_head' }
      },
      {
        type: 'invoice_large',
        conditions: { amountRange: { min: 200001, max: 1000000 } },
        approvers: [
          { level: 1, role: 'store_manager', required: true },
          { level: 2, role: 'branch_manager', required: true },
          { level: 3, role: 'regional_manager', required: true },
          { level: 4, role: 'sales_head', required: true }
        ],
        escalationRules: { timeoutHours: 72, escalateTo: 'ho_manager' }
      },
      {
        type: 'invoice_critical',
        conditions: { amountRange: { min: 1000001, max: Infinity } },
        approvers: [
          { level: 1, role: 'store_manager', required: true },
          { level: 2, role: 'branch_manager', required: true },
          { level: 3, role: 'regional_manager', required: true },
          { level: 4, role: 'sales_head', required: true },
          { level: 5, role: 'ho_manager', required: true },
          { level: 6, role: 'operations_head', required: true },
          { level: 7, role: 'director', required: true }
        ],
        escalationRules: { timeoutHours: 24, escalateTo: 'ceo' }
      }
    ],

    // Purchase Order Approvals
    purchase: [
      {
        type: 'purchase_routine',
        conditions: { amountRange: { min: 0, max: 100000 } },
        approvers: [
          { level: 1, role: 'inventory_manager', required: true },
          { level: 2, role: 'ho_manager', required: true }
        ],
        escalationRules: { timeoutHours: 24, escalateTo: 'operations_head' }
      },
      {
        type: 'purchase_significant',
        conditions: { amountRange: { min: 100001, max: 500000 } },
        approvers: [
          { level: 1, role: 'inventory_manager', required: true },
          { level: 2, role: 'ho_manager', required: true },
          { level: 3, role: 'operations_head', required: true }
        ],
        escalationRules: { timeoutHours: 48, escalateTo: 'director' }
      },
      {
        type: 'purchase_major',
        conditions: { amountRange: { min: 500001, max: Infinity } },
        approvers: [
          { level: 1, role: 'inventory_manager', required: true },
          { level: 2, role: 'ho_manager', required: true },
          { level: 3, role: 'operations_head', required: true },
          { level: 4, role: 'director', required: true },
          { level: 5, role: 'ceo', required: true }
        ],
        escalationRules: { timeoutHours: 72, escalateTo: 'board' }
      }
    ],

    // Stock Transfer Approvals
    transfer: [
      {
        type: 'transfer_internal',
        conditions: { amountRange: { min: 0, max: 200000 } },
        approvers: [
          { level: 1, role: 'branch_manager', required: true },
          { level: 2, role: 'inventory_manager', required: true }
        ],
        escalationRules: { timeoutHours: 12, escalateTo: 'ho_manager' }
      },
      {
        type: 'transfer_significant',
        conditions: { amountRange: { min: 200001, max: Infinity } },
        approvers: [
          { level: 1, role: 'branch_manager', required: true },
          { level: 2, role: 'inventory_manager', required: true },
          { level: 3, role: 'ho_manager', required: true },
          { level: 4, role: 'operations_head', required: true }
        ],
        escalationRules: { timeoutHours: 24, escalateTo: 'director' }
      }
    ],

    // Rate Change Approvals
    rate_change: [
      {
        type: 'rate_minor',
        conditions: { priority: 'low' },
        approvers: [
          { level: 1, role: 'ho_manager', required: true },
          { level: 2, role: 'operations_head', required: true }
        ],
        escalationRules: { timeoutHours: 6, escalateTo: 'director' }
      },
      {
        type: 'rate_major',
        conditions: { priority: 'high' },
        approvers: [
          { level: 1, role: 'ho_manager', required: true },
          { level: 2, role: 'operations_head', required: true },
          { level: 3, role: 'director', required: true }
        ],
        escalationRules: { timeoutHours: 2, escalateTo: 'ceo' }
      },
      {
        type: 'rate_critical',
        conditions: { priority: 'critical' },
        approvers: [
          { level: 1, role: 'ho_manager', required: true },
          { level: 2, role: 'operations_head', required: true },
          { level: 3, role: 'director', required: true },
          { level: 4, role: 'ceo', required: true }
        ],
        escalationRules: { timeoutHours: 1, escalateTo: 'board' }
      }
    ],

    // Warranty Claim Approvals
    warranty_claim: [
      {
        type: 'warranty_standard',
        conditions: { amountRange: { min: 0, max: 25000 } },
        approvers: [
          { level: 1, role: 'store_manager', required: true },
          { level: 2, role: 'qc_manager', required: true }
        ],
        escalationRules: { timeoutHours: 24, escalateTo: 'branch_manager' }
      },
      {
        type: 'warranty_complex',
        conditions: { amountRange: { min: 25001, max: Infinity } },
        approvers: [
          { level: 1, role: 'store_manager', required: true },
          { level: 2, role: 'qc_manager', required: true },
          { level: 3, role: 'branch_manager', required: true },
          { level: 4, role: 'ho_manager', required: true }
        ],
        escalationRules: { timeoutHours: 48, escalateTo: 'operations_head' }
      }
    ]
  } as Record<string, ApprovalRule[]>,

  // Get appropriate approval rule based on transaction type and conditions
  getApprovalRule: (type: string, amount?: number, priority?: string): ApprovalRule | null => {
    const typeRules = ApprovalRulesEngine.rules[type];
    if (!typeRules) return null;

    // Find matching rule based on conditions
    for (const rule of typeRules) {
      const { conditions } = rule;
      
      // Check amount range
      if (conditions.amountRange && amount !== undefined) {
        if (amount < conditions.amountRange.min || amount > conditions.amountRange.max) {
          continue;
        }
      }
      
      // Check priority
      if (conditions.priority && priority !== conditions.priority) {
        continue;
      }
      
      return rule;
    }
    
    return null;
  },

  // Authority levels for different roles
  roleAuthority: {
    'store_manager': 4,
    'branch_manager': 5,
    'manager_pobs': 5,
    'regional_manager': 6,
    'sales_head': 7,
    'ho_manager': 7,
    'inventory_manager': 6,
    'qc_manager': 6,
    'operations_head': 8,
    'director': 9,
    'ceo': 10
  } as Record<string, number>,

  // Check if user can approve at specific level
  canUserApprove: (userRole: string, requiredRole: string, amount?: number): boolean => {
    const userAuthority = ApprovalRulesEngine.roleAuthority[userRole] || 0;
    const requiredAuthority = ApprovalRulesEngine.roleAuthority[requiredRole] || 0;
    
    // User must have equal or higher authority
    if (userAuthority < requiredAuthority) return false;
    
    // Additional amount-based restrictions
    if (amount !== undefined) {
      // Store managers can't approve above 100k regardless of role
      if (userRole === 'store_manager' && amount > 100000) return false;
      
      // Branch managers can't approve above 500k
      if (userRole === 'branch_manager' && amount > 500000) return false;
      
      // Only director+ can approve above 2M
      if (amount > 2000000 && userAuthority < 9) return false;
    }
    
    return true;
  },

  // Generate approval workflow for a transaction
  generateWorkflow: (type: string, amount?: number, priority?: string) => {
    const rule = ApprovalRulesEngine.getApprovalRule(type, amount, priority);
    if (!rule) {
      throw new Error(`No approval rule found for type: ${type}`);
    }
    
    return {
      workflowId: `${type}_${Date.now()}`,
      type,
      amount,
      priority,
      rule,
      currentLevel: 1,
      maxLevel: rule.approvers.length,
      status: 'pending' as const,
      approvers: rule.approvers.map(approver => ({
        level: approver.level,
        role: approver.role,
        required: approver.required,
        canSkip: approver.canSkip || false
      })),
      escalationRules: rule.escalationRules,
      createdAt: new Date().toISOString()
    };
  }
};
